import axios, { CancelTokenSource } from 'axios';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import FormOption from '../../components/forms/formOption/FormOption';
import FormSelect from '../../components/forms/formSelect/FormSelect';
import Button from '../../components/forms/button/Button';
import download from 'downloadjs';
import { getGroupActivityStatsCsv } from '../../services/group';
import { fetchGroupActivityStats } from '../../store/group/groupActions';

interface IDispatchProps {
    fetchGroupActivityStats: typeof fetchGroupActivityStats;
}

export interface IRouterParams {
    groupId: string;
}

type Props = IDispatchProps & RouteComponentProps<IRouterParams>;

interface IOwnState {
    period: string;
    options: Highcharts.Options;
    isloading: boolean;
    cancelToken: CancelTokenSource;
    downloading: boolean;
    stats: any[]
}

class StatsIndex extends React.Component<Props, IOwnState> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isloading: true,
            period: "6",
            options: {},
            cancelToken: axios.CancelToken.source(),
            downloading: false,
            stats: []
        };
    }

    public async componentDidMount() {
        let statsResult = await this.fetchStats(),
            stats = statsResult.payload.result;

        this.setState({ stats: stats });

        let series: any[] = this.mapSeries(stats);

        this.setState({
            period: '6',
            options: {
                title: {
                    text: ''
                },
                plotOptions: {
                    series: {
                        stacking: 'normal'
                    }
                },
                xAxis: {
                    labels: {
                        useHTML: true,
                        formatter: this.getFormatter(stats)
                    },
                    categories: stats.map((s: any) => s.diNo)
                },
                yAxis: {
                    min: 0,
                    title: {
                        text: ''
                    }
                },
                series: series,
                credits: {
                    enabled: false
                },
                chart: {
                    styledMode: true,
                    spacingBottom: 80,
                    height: 50 * stats.length + 110
                },
                legend: {
                    enabled: true,
                    verticalAlign: 'bottom',
                    align: 'left',
                    y: 80,
                    itemDistance: 40,
                    useHTML: true,
                    labelFormatter: function () {
                        return '<span>' + this.name + '</span><span class="di-legend-item"><span class="di-legend-item__inner">' + this.name + '</span></span>';
                    },
                    itemMarginBottom: 30
                }
            },
            isloading: false
        });
    }

    public componentWillUnmount() {
        this.state.cancelToken.cancel();
    }

    public render() {
        return <React.Fragment>
            <section className="section">
                <div className="deck">
                    <div className="w--md banner-title">
                        <h1>Deltagerstatistik</h1>
                    </div>
                </div>
                <div className="deck">
                    <div className="w--md">
                        <div className="flex-grid">
                            <div className="flex-grid__col flex-grid__col--12 flex-grid__col--sm-4 flex-grid__col--sm-offset-8">
                                <div className="form">
                                    <div className="form__section">
                                        <FormSelect name="period" label="Vis aktivitet for" value={this.state.period} modifierClass="form__field--select--small" onChange={this.handlePeriodChange}>
                                            <FormOption value="3">Seneste 3 måneder</FormOption>
                                            <FormOption value="6">Seneste 6 måneder</FormOption>
                                            <FormOption value="12">Seneste 12 måneder</FormOption>
                                        </FormSelect>

                                    </div>
                                </div>
                            </div>
                        </div>
                        <hr className="spacing" />
                        <article className="article article--no-initial">
                            <div className="rich-text">
                                {(!this.state.isloading) &&
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={this.state.options}
                                        containerProps={{ className: 'di-graph', style: {} }}
                                    />
                                }
                            </div>
                            <div><Button modifierClass="button--small button--primary" disabled={this.state.downloading} onClick={this.handleClick} spinning={this.state.downloading} >Eksport til Excel</Button></div>
                        </article>
                    </div>
                </div>
            </section>
        </React.Fragment>;
    }

    private handleClick = async (e: React.FormEvent<HTMLButtonElement>) => {
        this.setState({
            downloading: true
        });

        const csv = await this.fetchStatsCsv(parseInt(this.state.period));

        download(csv,
            `deltagerstatistik-${this.props.match.params.groupId}.csv`,
            'text/csv');

        this.setState({
            downloading: false
        });
    }

    private handlePeriodChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedPeriod = e.target.value;
        let statsResult = await this.fetchStats(parseInt(selectedPeriod)),
            stats = statsResult.payload.result;

        this.setState({ stats: stats });

        let series: any[] = this.mapSeries(stats);

        this.setState({
            period: selectedPeriod,
            options: {
                xAxis: {
                    labels: {
                        useHTML: true,
                        formatter: this.getFormatter(stats)
                    },
                    categories: stats.map((s: any) => s.diNo)
                },
                series: series
            }
        });
    }

    private async fetchStats(months: number = 6): Promise<any> {
        let d = new Date();

        d.setMonth(d.getMonth() - months);

        return await this.props.fetchGroupActivityStats(this.props.match.params.groupId, d, this.state.cancelToken);
    }

    private async fetchStatsCsv(months: number): Promise<string> {
        let d = new Date();

        d.setMonth(d.getMonth() - months);

        return await getGroupActivityStatsCsv(this.props.match.params.groupId, d, this.state.cancelToken);
    }

    private mapSeries(stats: any): any[] {
        let series: any[] = [
            {
                id: '1',
                type: 'bar',
                name: 'Score møde',
                index: 3,
                legendIndex: 1,
                data: []
            },
            {
                id: '2',
                type: 'bar',
                name: 'Score email',
                index: 2,
                legendIndex: 2,
                data: []
            },
            {
                id: '3',
                type: 'bar',
                name: 'Score web',
                index: 1,
                legendIndex: 3,
                data: []
            }
        ];

        stats.forEach((s: any, i: number) => {
            series[0].data.push({ y: s.areaScores[0].value, name: s.areaScores[0].eventCounts.map((c: any) => this.mapEventText(c.event, c.count)).join(', ') });
            series[1].data.push({ y: s.areaScores[1].value, name: s.areaScores[1].eventCounts.map((c: any) => this.mapEventText(c.event, c.count)).join(', ') });
            series[2].data.push({ y: s.areaScores[2].value, name: s.areaScores[2].eventCounts.map((c: any) => this.mapEventText(c.event, c.count)).join(', ') });
        });

        return series;
    }

    private mapEventText(event: string, count: number) {
        switch (event) {
            case 'deltaget i møde': return 'deltaget i ' + count + ' møder'
            case 'mødesvar deltager måske': return 'svaret måske til ' + count + ' møder'
            case 'mødesvar tilmeldt': return 'tilmeldt ' + count + ' møder'
            case 'mødesvar frameldt': return 'frameldt ' + count + ' møder'
            case 'gruppe email læst': return 'læst ' + count + ' gruppe emails'
            case 'person email læst': return 'læst ' + count + ' person emails'
            case 'email læst': return 'læst ' + count + ' emails'
            case 'gruppe email sendt': return 'sendt ' + count + ' gruppe emails'
            case 'person email sendt': return 'sendt ' + count + ' person emails'
            case 'email sendt': return 'sendt ' + count + ' emails'
            case 'netværk besøg': return 'besøgt netværk' + count + ' gange'
            case 'dokument download': return 'downloaded ' + count + ' dokumenter'
            default: return ''
        }
    }

    private getFormatter(stats: any): Highcharts.FormatterCallbackFunction<Highcharts.AxisLabelsFormatterContextObject> | undefined {
        return function () {
            let person = stats.find((x: any) => x.diNo === this.value);
            let img = person.imageUrl ? '<div style="background-image:url(' + person.imageUrl + ')"></div>' : '<svg class="svg-icon svg - icon--person"><use xlink:href="/dist/svg/svg-sprite.svg#person" /></svg>';

            return '<span class="di-xaxis-label"><span class="di-xaxis-label__image">' + img + '</span><span class="di-xaxis-label__text">' + person.name + '</span></span>';
        };
    }
}

const mapDispatchToProps = (dispatch: any): IDispatchProps => ({
    fetchGroupActivityStats: (groupId: string, from: Date, cancelToken: CancelTokenSource = axios.CancelToken.source()) => dispatch(fetchGroupActivityStats(groupId, from, cancelToken))
});

const container = withRouter(connect(null, mapDispatchToProps)(StatsIndex));

export default container;